import { inject, injectable } from 'inversify'
import { action, computed, makeObservable } from 'mobx'
import { CommandPaletteRepository } from './command-palette.repository'
import { NavigationRepository } from '@core/infra/navigation/navigation.repository'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import type { CommandResult } from '@core/interfaces/command-source'

export type CommandResultViewModel = {
  key: string
  /** This item's position in the flattened, cross-group list — what `setActiveIndex` takes on hover. */
  index: number
  label: string
  preIcon?: string
  newsCount?: number
  isExternal: boolean
  path: string | null
  active: boolean
  select: () => void
}

export type CommandGroupViewModel = {
  key: string
  groupLabel: string
  showLoading: boolean
  showEmpty: boolean
  items: readonly CommandResultViewModel[]
}

export type QuickLinkViewModel = {
  key: string
  label: string
  preIcon?: string
  select: () => void
}

/**
 * Read/act side of the command palette: what CommandPaletteComponent renders
 * (query, one group per registered `CommandSource` with its own loading/empty
 * state, the active item's preview, footer quick links) and the
 * keyboard/click actions that drive it. Kept separate from
 * CommandPaletteRepository the same way I18nPresenter is kept separate from
 * I18nRepository — copy resolution and label rendering are a UI concern, not
 * state.
 *
 * Does not extend `FeedbackPresenter`: the palette has no failure surface
 * of its own to report against — a source's own search/load failure
 * belongs to that source's Repository (e.g. `SearchRepository`), not here.
 */
@injectable()
export class CommandPalettePresenter {
  @inject(CommandPaletteRepository)
  private repository!: CommandPaletteRepository

  @inject(NavigationRepository)
  private navigationRepository!: NavigationRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  constructor() {
    makeObservable(this, {
      isOpen: computed,
      viewModel: computed,
      open: action,
      close: action,
      setQuery: action,
      moveActive: action,
      setActiveIndex: action,
      selectActive: action,
    })
  }

  get isOpen(): boolean {
    return this.repository.openPm
  }

  get viewModel() {
    const t = this.i18nRepository.t.bind(this.i18nRepository)

    // Two passes: results have to be flattened once to know how many there
    // are (and so clamp `activeIndexPm` into range) before any one of them
    // can be marked `active` — a global position, not a per-group one.
    const rawGroups = this.repository.sources.map((source) => {
      const isLoading = source.state?.status === 'loading'
      return {
        sourceId: source.sourceId,
        groupLabel: t(source.groupLabelKey),
        showLoading: isLoading,
        results: source.results,
      }
    })
    const totalCount = rawGroups.reduce((sum, group) => sum + group.results.length, 0)
    const activeIndex = totalCount ? Math.min(this.repository.activeIndexPm, totalCount - 1) : 0

    let position = 0
    const groups: CommandGroupViewModel[] = rawGroups.map((group) => {
      const items = group.results.map((result) => {
        const item = this.toItemVm(result, position, position === activeIndex)
        position += 1
        return item
      })
      return {
        key: group.sourceId,
        groupLabel: group.groupLabel,
        showLoading: group.showLoading,
        showEmpty: !group.showLoading && items.length === 0,
        items,
      }
    })

    const items = groups.flatMap((group) => group.items)
    const anyLoading = groups.some((group) => group.showLoading)

    return {
      isOpen: this.repository.openPm,
      query: this.repository.queryPm,
      placeholder: t('shell.search'),
      emptyLabel: t('commandPalette.empty'),
      quickAccessLabel: t('commandPalette.quickAccess'),
      navigateHint: t('commandPalette.navigateHint'),
      selectHint: t('commandPalette.selectHint'),
      closeHint: t('commandPalette.closeHint'),
      pathLabel: t('routeInfo.path'),
      unreadLabel: t('commandPalette.unread'),
      externalLabel: t('commandPalette.external'),
      searchingLabel: t('commandPalette.searching'),
      groups,
      showEmpty: items.length === 0 && !anyLoading,
      items,
      activeIndex,
      active: items[activeIndex] ?? null,
      // First four of whatever the current query already narrowed things to
      // — not a separate always-unfiltered shortcut list, so quick access
      // never contradicts what was just typed.
      quickLinks: items.slice(0, 4).map((item): QuickLinkViewModel => ({
        key: item.key,
        label: item.label,
        preIcon: item.preIcon,
        select: item.select,
      })),
    }
  }

  open = () => {
    this.repository.open()
  }

  close = () => {
    this.repository.close()
  }

  /** ⌘K/Ctrl+K's decision (CI-5: not the Component's) — same key opens and closes. */
  toggle = () => {
    if (this.isOpen) this.close()
    else this.open()
  }

  setQuery = (query: string) => {
    this.repository.setQuery(query)
  }

  setActiveIndex = (index: number) => {
    this.repository.setActiveIndex(index)
  }

  moveActive = (delta: number) => {
    const length = this.viewModel.items.length
    if (length === 0) return
    const next = (((this.repository.activeIndexPm + delta) % length) + length) % length
    this.repository.setActiveIndex(next)
  }

  selectActive = () => {
    this.viewModel.active?.select()
  }

  private toItemVm(result: CommandResult, index: number, active: boolean): CommandResultViewModel {
    const label = result.label.kind === 'i18n' ? this.i18nRepository.t(result.label.key) : result.label.text
    const isExternal = result.kind === 'external'
    const path = result.kind === 'navigate' ? this.repository.pathFor(result.routeId) : result.url
    return {
      key: result.key,
      index,
      label,
      preIcon: result.preIcon,
      newsCount: result.kind === 'navigate' ? result.newsCount : undefined,
      isExternal,
      path,
      active,
      select: () => this.choose(result),
    }
  }

  private choose = (result: CommandResult) => {
    if (result.kind === 'external') {
      void this.navigationRepository.follow({ urlTo: result.url })
    } else {
      void this.navigationRepository.follow({ routesTo: result.routeId, params: result.params })
    }
    this.repository.close()
  }
}
